import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  formatArea,
  formatLength,
  source,
  vector,
} from 'components/MapComponent/MapUtils/MeasurementMap/utils';
import Draw from 'ol/interaction/Draw';
import Overlay from 'ol/Overlay';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { LineString, Polygon } from 'ol/geom';
import { unByKey } from 'ol/Observable';
import { MapBrowserEvent } from 'ol/src/MapBrowserEvent.js';
import { Coordinate } from 'ol/coordinate';
import MapContext from 'components/MapComponent/MapContext';
import { getText } from 'services/languageService';

let sketch: any;

let helpTooltipElement: HTMLElement;
let helpTooltip: Overlay;
let measureTooltipElement: HTMLElement | null;
let measureTooltip: Overlay;
const continuePolygonMsg: string = 'Нажмите для продолжения измерения площади';

let draw: Draw; // global so we can remove it later

export const pointerMoveHandler = function (evt: MapBrowserEvent) {
  if (evt.dragging) {
    return;
  }
  let helpMsg: string = getText('START_MEASURE_TL');
  const continueLineMsg: string = getText('CONTINUE_MEASURE_TL');

  if (sketch) {
    const geom = sketch.getGeometry();
    if (geom instanceof Polygon) {
      helpMsg = continuePolygonMsg;
    } else if (geom instanceof LineString) {
      helpMsg = continueLineMsg;
    }
  }

  helpTooltipElement.innerHTML = helpMsg;
  helpTooltip.setPosition(evt.coordinate);

  helpTooltipElement.classList.remove('hidden');
};

function hiddenTooltip() {
  helpTooltipElement.classList.add('hidden');
}

function createMeasureTooltip(map: any, lastTooltip: any) {
  if (measureTooltipElement) {
    measureTooltipElement.parentNode?.removeChild(measureTooltipElement);
  }

  measureTooltipElement = document.createElement('div');
  measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
  measureTooltip = new Overlay({
    element: measureTooltipElement,
    offset: [0, -15],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false,
  });
  map.addOverlay(measureTooltip);

  lastTooltip.current = measureTooltip;
}

function createHelpTooltip(map: any) {
  if (helpTooltipElement) {
    helpTooltipElement.parentNode?.removeChild(helpTooltipElement);
  }
  helpTooltipElement = document.createElement('div');
  helpTooltipElement.className = 'ol-tooltip hidden';
  helpTooltip = new Overlay({
    element: helpTooltipElement,
    offset: [15, 0],
    positioning: 'center-left',
  });

  map.addOverlay(helpTooltip);
}

export const MeasurementMap: React.FC = (): null => {
  const { map } = useContext(MapContext);
  const { ruler } = useAppSelector((state) => state.measurements.mode);
  const prelastTooltip = useRef<Overlay | null>(null);
  const lastTooltip = useRef<Overlay | null>(null);
  const dispatch = useAppDispatch();

  const addInteraction = useCallback(() => {
    draw = new Draw({
      source: source,
      type: 'LineString',
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)',
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
          }),
        }),
      }),
    });
    map.addInteraction(draw);

    createMeasureTooltip(map, lastTooltip);
    createHelpTooltip(map);

    let listener: any;

    draw.on('drawstart', function (evt: MapBrowserEvent) {
      sketch = evt.feature;

      // !it works don't touch this
      if (prelastTooltip.current) {
        map.removeOverlay(prelastTooltip.current);
      }

      if (lastTooltip.current) {
        prelastTooltip.current = lastTooltip.current;
        lastTooltip.current = null;
      }
      source.clear(); //clear previous measure

      let tooltipCoord: Coordinate = evt.coordinate;

      listener = sketch
        .getGeometry()
        .on('change', function (evt: MapBrowserEvent) {
          const geom = evt.target;
          let output = '';
          if (geom instanceof Polygon) {
            output = formatArea(geom, (output) => {});
            const allCoordinates = evt.target.getCoordinates()[0];
            tooltipCoord = allCoordinates[allCoordinates.length - 2];
          } else if (geom instanceof LineString) {
            output = formatLength(geom, (output) => {});
            tooltipCoord = geom.getLastCoordinate();
          }
          measureTooltipElement!.innerHTML = output;
          measureTooltip.setPosition(tooltipCoord);
        });
    });

    draw.on('drawend', function () {
      measureTooltipElement!.className = 'ol-tooltip ol-tooltip-static';
      measureTooltip.setOffset([0, -7]);
      sketch = null;

      measureTooltipElement = null;
      createMeasureTooltip(map, lastTooltip);
      unByKey(listener);
    });
  }, [ruler, map]);

  //when component unmount
  const unmountComponent = useCallback(() => {
    map.removeLayer(vector);
    map.removeOverlay(helpTooltip);
    map.removeOverlay(prelastTooltip.current);

    map.un('pointermove', pointerMoveHandler);
    map.getViewport().removeEventListener('mouseout', hiddenTooltip);
    map.removeInteraction(draw);
    source.clear(); // clear field when change tool
  }, [ruler, map]);

  //when component mount
  useEffect(() => {
    if (!ruler) return;
    map.addLayer(vector);
    map.on('pointermove', pointerMoveHandler);

    map.getViewport().addEventListener('mouseout', hiddenTooltip);

    map.removeInteraction(draw);

    addInteraction();

    return unmountComponent;
  }, [ruler, map]);

  return null;
};
